import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, ArrowLeft, Check, Building2, ClipboardList, Compass, Handshake, Users, TrendingUp, Plus } from "lucide-react";
import useContentTranslation from "@/hooks/useContentTranslation";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const iconMap = { Building2, ClipboardList, Compass, Handshake, Users, TrendingUp };

export function ServicesList() {
  const [services, setServices] = useState([]);
  const tl = useContentTranslation();
  useSEO({
    title: "Nos Prestations",
    description: "Découvrez les prestations d'AMAR SAHRAOUI : promotion immobilière résidentielle, développement de programmes neufs, conception et pilotage, commercialisation, accompagnement et valorisation foncière à Oran.",
    path: "/prestations",
  });

  useEffect(() => {
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 border-b atelier-border-soft bg-atelier-deep relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="px-5 lg:px-10 relative z-10">
          <div className="label-tech text-atelier-ink mb-4">— Nos prestations</div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-none text-atelier-text max-w-4xl">
            Un savoir-faire intégré, <span className="italic text-atelier-ink">de A à Z.</span>
          </h1>
          <p className="mt-8 text-lg text-atelier-textDim max-w-2xl leading-relaxed font-serif">
            De la valorisation foncière à la commercialisation, nous orchestrons chaque étape de la promotion immobilière avec rigueur et passion.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-atelier">
        <div className="px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 atelier-border">
          {services.map((s, idx) => {
            const Icon = iconMap[s.icon] || Building2;
            return (
              <Reveal key={s.id} delay={idx * 80}>
                <Link
                  to={`/prestations/${s.slug}`}
                  className="scan-card p-8 lg:p-10 group hover:bg-atelier-surface/60 transition-all duration-500 border-r border-b atelier-border-soft block h-full"
                  data-testid={`service-tile-${s.slug}`}
                >
                  <Icon className="w-10 h-10 text-atelier-ink mb-8 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.3} />
                  <h3 className="label-tech text-atelier-text text-sm mb-3">{tl(s, "title")}</h3>
                  <p className="text-sm text-atelier-textDim leading-relaxed mb-6 font-serif">{tl(s, "short_description")}</p>
                  <div className="label-tech text-atelier-ink flex items-center gap-2 group-hover:gap-3 transition-all">
                    <Plus className="w-3 h-3" /> Découvrir <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const tl = useContentTranslation();
  useSEO({
    title: service ? tl(service, "title") : "Prestation",
    description: service ? tl(service, "short_description") : undefined,
  });

  useEffect(() => {
    api.get(`/public/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound />;
  if (!service) return <div className="py-32 text-center text-atelier-textDim">Chargement…</div>;

  const Icon = iconMap[service.icon] || Building2;

  return (
    <div>
      <section className="relative h-[55vh] min-h-[400px] flex items-end bg-atelier-deep overflow-hidden">
        {service.image_url && <img src={service.image_url} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="absolute inset-0 bg-gradient-to-t from-atelier-deep via-atelier-deep/60 to-transparent" />
        <div className="absolute inset-0 blueprint-grid opacity-15" aria-hidden="true" />
        <div className="relative px-5 lg:px-10 pb-14 w-full">
          <Link to="/prestations" className="inline-flex items-center gap-2 label-tech text-atelier-textDim hover:text-atelier-ink transition-colors mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Toutes les prestations
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <Icon className="w-9 h-9 text-atelier-ink" strokeWidth={1.3} />
            <div className="label-tech text-atelier-ink">{tl(service, "subtitle")}</div>
          </div>
          <h1 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[1.02] text-atelier-text max-w-4xl">{tl(service, "title")}</h1>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-atelier">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <p className="text-lg leading-relaxed text-atelier-textDim whitespace-pre-line font-serif">{tl(service, "long_description")}</p>
          </div>
          <aside className="md:col-span-4">
            {service.advantages?.length > 0 && (
              <div className="border-l-2 border-atelier-ink pl-6">
                <div className="label-tech text-atelier-ink mb-4">Avantages</div>
                <ul className="space-y-3">
                  {service.advantages.map((a) => (
                    <li key={a} className="flex gap-3 text-sm text-atelier-text font-serif">
                      <Check className="w-4 h-4 text-atelier-ink flex-shrink-0 mt-0.5" strokeWidth={1.4} /> <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {service.zone && (
              <div className="mt-8 atelier-border p-6 bg-atelier-surface/40">
                <div className="label-tech text-atelier-ink mb-2">Zone d'intervention</div>
                <div className="font-serif text-lg text-atelier-text">{service.zone}</div>
              </div>
            )}
            <Link to="/contact" className="mt-8 btn-primary w-full justify-center" data-testid="service-cta-contact">
              Demander des informations
            </Link>
          </aside>
        </div>
      </section>
    </div>
  );
}

function NotFound() {
  return (
    <div className="py-32 text-center max-w-xl mx-auto px-5">
      <h2 className="font-serif text-4xl mb-4 text-atelier-text">Page introuvable</h2>
      <Link to="/prestations" className="btn-primary mt-4 inline-flex">Retour aux prestations</Link>
    </div>
  );
}
